import { toast } from 'sonner';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUpdateAssetMissionPrevalence } from '@/hooks/useAssets';
import { Asset } from '@/types';

const MISSION_PREVALENCE_OPTIONS = [
  {
    value: 'minimal',
    label: 'Minimal',
    description: 'Little to no impact on mission capability',
  },
  {
    value: 'support',
    label: 'Support',
    description: 'Supports but is not essential to mission',
  },
  {
    value: 'essential',
    label: 'Essential',
    description: 'Critical to mission success',
  },
] as const;

export interface MissionPrevalenceSectionProps {
  asset: Asset;
}

export function MissionPrevalenceSection({
  asset,
}: MissionPrevalenceSectionProps) {
  const { mutateAsync: updateMissionPrevalence, status: mpStatus } =
    useUpdateAssetMissionPrevalence();

  const handleMissionPrevalenceChange = async (value: string) => {
    if (value === asset.mission_prevalence) return;
    try {
      await updateMissionPrevalence({
        key: asset.key,
        mission_prevalence: value as 'minimal' | 'support' | 'essential',
      });
      toast.success('Mission prevalence updated');
    } catch {
      toast.error('Failed to update mission prevalence');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-lg font-bold">SSVC Deployer Inputs</h1>
        <p className="text-chariot-text-secondary text-sm">
          Configure per-asset inputs for the SSVC deployer decision tree
        </p>
      </div>
      <div className="flex flex-wrap gap-6">
        <div className="flex flex-col gap-1.5">
          <label className="text-chariot-text-secondary text-xs font-medium">
            Mission Prevalence
          </label>
          <Select
            value={asset.mission_prevalence || ''}
            onValueChange={handleMissionPrevalenceChange}
            disabled={mpStatus === 'pending'}
          >
            <SelectTrigger
              className="w-48"
              data-testid="mission-prevalence-select"
            >
              <SelectValue placeholder="Not set" />
            </SelectTrigger>
            <SelectContent>
              {MISSION_PREVALENCE_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  <span className="font-medium">{option.label}</span>
                  <span className="text-chariot-text-secondary ml-2 text-xs">
                    {option.description}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
